import { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import MetricCard from '../components/ui/MetricCard';
import Table, { TableLink } from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import Toast from '../components/ui/Toast';
import { fetchWithAdminAuth } from '../utils/adminAuth';
import type { Metric } from '../types';

interface AIMetrics {
  totalRevenueWithIva: number;
  totalRevenueWithoutIva: number;
  totalCost: number;
  margin: number;
  marginPercentage: number;
  revenueChangePercent: number;
  costPercentage: number;
  totalTokensUsed?: number;
  totalSessions?: number;
}

interface AISession {
  id: string;
  date: string;
  clientName: string;
  clientEmail?: string;
  product: string;
  duration?: number;
  revenueWithIva: number;
  revenueWithoutIva: number;
  cost: number;
  margin: number;
  totalTokens?: number;
  promptTokens?: number;
  completionTokens?: number;
}

interface DailySummary {
  date: string;
  sessionsCount: number;
  revenueWithoutIva: number;
  cost: number;
  margin: number;
  tokens?: number;
}

interface ConversationDetail {
  id: string;
  status: string;
  consultantName: string;
  duration: number;
  paidClient: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  aiCost: number;
  startDate: string;
  endDate: string | null;
  user: { id: string; name: string; email: string; credits: number };
  consultant: { id: string; name: string; type: string[]; pricePerMin: number };
  messages: { id: string; role: string; content: string; createdAt: string }[];
}

const PERIODS = [
  { value: 'today', label: 'Hoje' },
  { value: 'week', label: 'Esta semana' },
  { value: 'month', label: 'Este mês' },
  { value: 'quarter', label: 'Este trimestre' },
  { value: 'year', label: 'Este ano' }
];

export default function RelatorioIA() {
  const [period, setPeriod] = useState('month');
  const [aiMetrics, setAiMetrics] = useState<AIMetrics | null>(null);
  const [sessions, setSessions] = useState<AISession[]>([]);
  const [dailySummary, setDailySummary] = useState<DailySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Session detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [metricsRes, dailyRes, sessionsRes] = await Promise.all([
        fetchWithAdminAuth(`/api/admin/ai-report/metrics?period=${period}`),
        fetchWithAdminAuth(`/api/admin/ai-report/daily?period=${period}`),
        fetchWithAdminAuth(`/api/admin/ai-report/sessions?period=${period}&limit=20`)
      ]);
      
      if (metricsRes.ok) {
        setAiMetrics(await metricsRes.json());
      }
      if (dailyRes.ok) {
        setDailySummary(await dailyRes.json());
      }
      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setSessions(sessionsData?.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching AI report data:', error);
      setToast({ message: 'Erro ao carregar dados', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openSessionDetail = async (sessionId: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await fetchWithAdminAuth(`/api/admin/conversations/${sessionId}`);
      if (res.ok) {
        setDetail(await res.json());
      }
    } catch (err) {
      console.error("Error loading session detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await fetchWithAdminAuth(`/api/admin/ai-report/export?period=${period}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-report-${period}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        setToast({ message: 'Relatório exportado com sucesso!', type: 'success' });
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      setToast({ message: 'Erro ao exportar relatório', type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatCost = (value: number) => {
    // Show cost in USD with more precision for small AI costs
    return `$${value.toFixed(4)}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', ' ·');
  };

  const formatDateShort = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  const metrics: Metric[] = aiMetrics ? [
    {
      label: 'Faturação IA (com IVA)',
      value: formatCurrency(aiMetrics.totalRevenueWithIva),
      change: { 
        value: `${aiMetrics.revenueChangePercent >= 0 ? '+' : ''}${aiMetrics.revenueChangePercent.toFixed(1)}% vs. período anterior`, 
        type: aiMetrics.revenueChangePercent >= 0 ? 'up' : 'down' 
      },
      footer: { left: 'Inclui IVA a 23%', right: `${aiMetrics.totalSessions ?? 0} sessões` }
    },
    {
      label: 'Faturação IA (sem IVA)',
      value: formatCurrency(aiMetrics.totalRevenueWithoutIva),
      change: { 
        value: `${aiMetrics.revenueChangePercent >= 0 ? '+' : ''}${aiMetrics.revenueChangePercent.toFixed(1)}% vs. período anterior`, 
        type: aiMetrics.revenueChangePercent >= 0 ? 'up' : 'down' 
      },
      footer: { left: 'Base tributável', right: 'Exportar mapa' }
    },
    {
      label: 'Custo real ChatGPT',
      value: `$${aiMetrics.totalCost.toFixed(2)}`,
      change: { 
        value: `${aiMetrics.costPercentage.toFixed(1)}% da receita líquida`, 
        type: 'down' 
      },
      footer: { left: `${(aiMetrics.totalTokensUsed ?? 0).toLocaleString()} tokens`, right: 'Dados reais por sessão' }
    },
    {
      label: 'Margem IA',
      value: formatCurrency(aiMetrics.margin),
      change: { 
        value: `${aiMetrics.marginPercentage.toFixed(1)}% de margem`, 
        type: 'up' 
      },
      footer: { left: 'Receita líquida - custo IA', right: '' }
    }
  ] : [];

  const sessionColumns = [
    { header: 'Data/hora', key: 'date', render: (val: string) => formatDate(val) },
    { header: 'Cliente', key: 'clientName' },
    { header: 'Produto', key: 'product' },
    { header: 'Faturação c/ IVA', key: 'revenueWithIva', render: (val: number) => formatCurrency(val) },
    { header: 'Custo IA', key: 'cost', render: (val: number) => formatCost(val) },
    { header: 'Tokens', key: 'totalTokens', render: (val: number) => (val || 0).toLocaleString() },
    { header: 'Margem', key: 'margin', render: (val: number) => formatCurrency(val) },
    { header: '', key: 'actions', render: (_val: any, row: AISession) => (
      <TableLink onClick={() => openSessionDetail(row.id)}>Ver sessão</TableLink>
    )}
  ];

  const dailyColumns = [
    { header: 'Data', key: 'date', render: (val: string) => formatDateShort(val) },
    { header: 'Consultas IA', key: 'sessionsCount' },
    { header: 'Faturação s/ IVA', key: 'revenueWithoutIva', render: (val: number) => formatCurrency(val) },
    { header: 'Custo IA', key: 'cost', render: (val: number) => formatCost(val) },
    { header: 'Tokens', key: 'tokens', render: (val: number) => (val || 0).toLocaleString() },
    { header: 'Margem', key: 'margin', render: (val: number) => formatCurrency(val) }
  ];

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5 flex flex-col gap-4 sm:gap-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Card
        title="Resumo financeiro das consultas IA"
        subtitle="Faturação, custos reais do ChatGPT (tokens usados) e margem obtida no período selecionado."
        action={
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-1.5 rounded-full border border-border-soft bg-[#0a0b14]/90 text-[0.75rem] text-text-soft outline-none"
            >
              {PERIODS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <Button onClick={fetchData} disabled={loading}>↺ Atualizar</Button>
            <Button variant="primary" onClick={handleExport} disabled={exporting}>
              {exporting ? 'A exportar...' : '⇩ Exportar para Excel'}
            </Button>
          </div>
        }
      >
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-1">
            {metrics.map((metric, idx) => (
              <MetricCard key={idx} metric={metric} />
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)] gap-4">
        <Card
          title="Consultas IA por sessão"
          subtitle="Cada linha representa uma sessão de IA com faturação, custo real e tokens usados."
        >
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <>
              <Table columns={sessionColumns} data={sessions} />
              {sessions.length === 0 && (
                <div className="text-center py-6 text-gray-500 text-sm">
                  Nenhuma sessão encontrada para o período selecionado
                </div>
              )}
            </>
          )}
        </Card>

        <Card
          title="Resumo por dia"
          subtitle="Visão agregada de faturação, custo IA real e margem por dia."
        >
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <>
              <div className="mb-3">
                <Table columns={dailyColumns} data={dailySummary} />
              </div>
              {dailySummary.length === 0 && (
                <div className="text-center py-6 text-gray-500 text-sm">
                  Nenhum dado para o período selecionado
                </div>
              )}
            </>
          )}
          <div className="h-[180px] rounded-xl border border-dashed border-border-soft bg-[radial-gradient(circle_at_top,rgba(226,183,20,0.08),transparent_55%)] flex items-center justify-center text-[0.78rem] text-text-soft mt-1.5">
            Aqui o programador pode colocar um gráfico de linhas com
            Faturação s/ IVA, Custo IA e Margem por dia.
          </div>
        </Card>
      </div>

      {/* Session Detail Modal */}
      <Modal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Detalhes da Sessão IA"
      >
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          </div>
        ) : detail ? (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[0.78rem]">
              <div>
                <span className="text-text-soft block">Cliente</span>
                <span className="text-text font-medium">{detail.user.name}</span>
                <span className="text-text-soft block text-xs">{detail.user.email}</span>
              </div>
              <div>
                <span className="text-text-soft block">Consultor</span>
                <span className="text-text font-medium">{detail.consultantName}</span>
              </div>
              <div>
                <span className="text-text-soft block">Duração</span>
                <span className="text-text font-medium">{formatDuration(detail.duration)}</span>
              </div>
              <div>
                <span className="text-text-soft block">Pago pelo cliente</span>
                <span className="text-text font-medium">€{detail.paidClient.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-text-soft block">Custo IA real</span>
                <span className="text-text font-medium text-yellow-400">${detail.aiCost.toFixed(6)}</span>
              </div>
              <div>
                <span className="text-text-soft block">Tokens usados</span>
                <span className="text-text font-medium">{detail.totalTokens.toLocaleString()}</span>
                <span className="text-text-soft block text-xs">
                  {detail.promptTokens.toLocaleString()} in / {detail.completionTokens.toLocaleString()} out
                </span>
              </div>
              <div>
                <span className="text-text-soft block">Início</span>
                <span className="text-text font-medium text-xs">
                  {new Date(detail.startDate).toLocaleString("pt-PT")}
                </span>
              </div>
              <div>
                <span className="text-text-soft block">Fim</span>
                <span className="text-text font-medium text-xs">
                  {detail.endDate ? new Date(detail.endDate).toLocaleString("pt-PT") : 'Em curso'}
                </span>
              </div>
            </div>

            {/* Chat messages */}
            <div>
              <h4 className="text-[0.82rem] font-medium text-text mb-2 border-t border-border-soft pt-3">
                Mensagens ({detail.messages.length})
              </h4>
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {detail.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`rounded-lg px-3 py-2 text-[0.75rem] ${
                      msg.role === 'user'
                        ? 'bg-purple-500/10 border border-purple-500/20 ml-8'
                        : 'bg-white/5 border border-border-soft mr-8'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className={`font-medium text-[0.7rem] ${
                        msg.role === 'user' ? 'text-purple-300' : 'text-green-300'
                      }`}>
                        {msg.role === 'user' ? '👤 Cliente' : '🤖 Consultor IA'}
                      </span>
                      <span className="text-text-soft text-[0.65rem]">
                        {new Date(msg.createdAt).toLocaleTimeString("pt-PT", { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-text whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-text-soft">Sessão não encontrada</div>
        )}
      </Modal>
    </div>
  );
}
